import {
  Card,
  Col,
  Popover,
  Row,
  Space,
  Statistic,
  Typography,
  Modal,
  Upload,
  Table,
  notification,
  TablePaginationConfig,
} from "antd";
import {
  EditOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  useGetProviderDocumentstatsQuery,
  useGetProviderQuery,
} from "../../redux/api/provider/provider";

import { skipToken } from "@reduxjs/toolkit/query";
import IncredableButton from "../../components/button";
import { KeyValuePairs } from "@cloudscape-design/components";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useCallback, useMemo, useState, useEffect, useContext } from "react";
import EditProvider from "../modify/edit-provider";
import { useSaveDocumentMutation } from "../../redux/api/document/document";
import { DocumentRequest } from "../../redux/api/document/types";
import { fileAxios } from "../../context/axios";
import { getFileUrl } from "../../config";
import { ToastContext } from "../../context/toast/incredable-toast";
import { SpinnerContext } from "../../context/spinner/spinner";
const { Dragger } = Upload;

interface DocumentRequestWithKey extends DocumentRequest {
  key: string;
}

type UploadProgressItem = {
  fileName: string;
  progress: number;
  status: "uploading" | "success" | "error";
};
export function Overview() {
  const { providerId } = useParams<{ providerId?: string }>();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [fileList, setFileList] = useState<File[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });

  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 5,
    });
  };

  const [uploadProgress, setUploadProgress] = useState<UploadProgressItem[]>(
    []
  );
  const [saveDocument] = useSaveDocumentMutation();
  const [isLoading, setIsLoading] = useState(false);

  const { data: selectedProvider } = useGetProviderQuery(
    providerId ? { providerId } : skipToken
  );

  const toggleDrawer = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleEditClick = useCallback(() => {
    if (providerId) setIsOpen(true);
  }, [providerId]);

  const handleBulkUploadClick = () => {
    setIsModalVisible(true);
  };

  const uploadFiles = useCallback(
    async (files: DocumentRequestWithKey[]) => {
      setIsLoading(true);
      setUploadProgress(
        files.map((item) => ({
          fileName: item.name,
          progress: 0,
          status: "uploading" as const,
        }))
      );

      for (let index = 0; index < files.length; index++) {
        const item = files[index];

        try {
          const documentResponse = await saveDocument({
            document: item,
          }).unwrap();

          if (item.file && documentResponse?.attachment?.key) {
            await fileAxios.put(
              getFileUrl(documentResponse.attachment.key),
              item.file,
              {
                onUploadProgress: (progressEvent) => {
                  const percentCompleted = progressEvent.total
                    ? Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                      )
                    : 0;
                  setUploadProgress((prev) =>
                    prev.map((p, i) =>
                      i === index ? { ...p, progress: percentCompleted } : p
                    )
                  );
                },
              }
            );

            setUploadProgress((prev) =>
              prev.map((p, i) =>
                i === index ? { ...p, status: "success" } : p
              )
            );
          }
        } catch (error) {
          setUploadProgress((prev) =>
            prev.map((p, i) => (i === index ? { ...p, status: "error" } : p))
          );
        }
      }

      setIsLoading(false);
    },
    [saveDocument]
  );
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const handleUpload = useCallback(async () => {
    if (!providerId) return;
    const documents: DocumentRequestWithKey[] = fileList.map((file, idx) => ({
      key: `${file.name}-${file.lastModified}-${idx}`,
      name: file.name,
      file,
      providerId,
    }));
    setIsModalVisible(false);
    spinnerContext?.showSpinner();
    try {
      await uploadFiles(documents);
    } catch (error: any) {
      toastContext?.openErrorNotification({
        message: "Upload Failed",
        placement: "topRight",
      });
    } finally {
      spinnerContext?.hidespinner();
      setFileList([]);
    }
  }, [fileList, providerId, uploadFiles]);

  useEffect(() => {
    if (uploadProgress.length > 0) {
      notification.open({
        key: "upload-progress",
        message: "File Upload Progress",
        description: (
          <div>
            {uploadProgress.map((prog) => (
              <div key={prog.fileName} style={{ marginBottom: 8 }}>
                <div>{prog.fileName}</div>
                <div>
                  {prog.status === "uploading" && (
                    <span style={{ color: "#1890ff" }}>
                      Progress: {prog.progress}% - Uploading...
                    </span>
                  )}
                  {prog.status === "success" && (
                    <span style={{ color: "#52c41a" }}>Upload Complete</span>
                  )}
                  {prog.status === "error" && (
                    <span style={{ color: "red" }}>Upload Failed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ),
        placement: "bottomRight",
        duration: 0,
      });
    } else {
      notification.destroy("upload-progress");
    }
  }, [uploadProgress]);

  const generalInfo = useMemo(
    () => [
      { label: "First Name", value: selectedProvider?.firstName || "N/A" },
      { label: "Last Name", value: selectedProvider?.lastName || "N/A" },
      { label: "Email", value: selectedProvider?.email || "N/A" },
      { label: "NPI Number", value: selectedProvider?.npiNumber || "N/A" },
      {
        label: "Facility Group",
        value: selectedProvider?.facilityGroup?.name || "N/A",
      },
      { label: "Facility", value: selectedProvider?.facility?.name || "N/A" },
      {
        label: "License Type",
        value: selectedProvider?.licenseType?.name || "N/A",
      },
      {
        label: "Employment Status",
        value: selectedProvider?.providerStatus?.status || "N/A",
      },
    ],
    [selectedProvider]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <Card
        title={<span style={{ fontSize: "18px" }}>General Information</span>}
        extra={
          <>
            <IncredableButton
              type="primary"
              icon={<EditOutlined />}
              iconPosition="start"
              onClick={handleEditClick}
            >
              Edit
            </IncredableButton>
            <IncredableButton
              onClick={handleBulkUploadClick}
              style={{ marginLeft: "10px" }}
            >
              Bulk Uploads
            </IncredableButton>
          </>
        }
      >
        <KeyValuePairs id="keyvaluepair" columns={3} items={generalInfo} />
      </Card>

      {isOpen && selectedProvider && (
        <Drawer
          open={isOpen}
          onClose={toggleDrawer}
          direction="right"
          size="90vw"
        >
          <EditProvider
            key={selectedProvider?.id}
            providerId={selectedProvider?.id}
            toggleDrawer={toggleDrawer}
          />
        </Drawer>
      )}

      <Modal
        title={
          <div style={{ marginTop: "10px" }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Upload Documents
            </Typography.Title>
            <Typography.Text type="secondary">
              Drag and drop or select files to begin uploading
            </Typography.Text>
          </div>
        }
        style={{ top: 20 }}
        open={isModalVisible}
        onCancel={() => {
          setFileList([]);
          setIsModalVisible(false);
        }}
        footer={null}
        width={1000}
        bodyStyle={{
          overflowY: "auto",
          scrollbarWidth: "none",
        }}
      >
        {fileList.length === 0 && (
          <Card
            style={{
              borderRadius: "16px",
              boxShadow: "0px 10px 25px 0px #5911920D",
              marginBottom: "20px",
            }}
          >
            <Dragger
              beforeUpload={(file) => {
                setFileList((prev) => [...prev, file]);
                return false;
              }}
              fileList={[]}
              multiple
              showUploadList={false}
              style={{
                border: "1px dashed #598BEB",
                background: "#F6FAFF",
                height: "200px",
              }}
            >
              <Space direction="vertical">
                <Space>
                  <InboxOutlined
                    style={{ fontSize: "32px", color: "#7C4095" }}
                  />
                  <span>Drag and Drop your file(s) to start uploading</span>
                </Space>
                OR
                <IncredableButton
                  style={{
                    background: "#598BEB",
                    color: "#FFF",
                    borderColor: "#598BEB",
                  }}
                >
                  Select a File
                </IncredableButton>
              </Space>
            </Dragger>
          </Card>
        )}

        {fileList.length > 0 && (
          <>
            <Table
              dataSource={fileList.map((file, index) => ({
                key: index,
                name: file.name,
                size: `${(file.size / 1024).toFixed(2)} KB`,
              }))}
              columns={[
                { title: "File Name", dataIndex: "name" },
                { title: "Size", dataIndex: "size" },
              ]}
              pagination={
                fileList.length > 5
                  ? {
                      ...pagination,
                      total: fileList.length,
                      showSizeChanger: true,
                      pageSizeOptions: ["5", "10", "15"],
                    }
                  : false
              }
              onChange={handleTableChange}
              style={{ marginBottom: "20px" }}
            />

            <IncredableButton
              onClick={handleUpload}
              disabled={isLoading}
              style={{
                background: "#598BEB",
                color: "#FFF",
                width: "100%",
              }}
            >
              Submit
            </IncredableButton>
          </>
        )}
      </Modal>
    </>
  );
}

export function ProviderDocumentSummary() {
  const { providerId } = useParams();
  const { data: providerDocumentsStats } = useGetProviderDocumentstatsQuery(
    !!providerId ? { providerID: providerId } : skipToken
  );

  return (
    <Card
      title={
        <Typography.Title
          level={3}
          style={{ marginTop: "0", fontSize: "19px" }}
        >
          Documents Summary
        </Typography.Title>
      }
      extra={
        <IncredableButton type="primary">
          <a href="#safe"> Safe</a>
        </IncredableButton>
      }
    >
      <Row gutter={16}>
        <Col span={8}>
          <Card bordered={true}>
            <Statistic
              title="Total count"
              value={providerDocumentsStats?.totalDocument ?? "0"}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true}>
            <Statistic
              title={
                <Space>
                  Expiring count
                  <Popover
                    content={
                      "Documents with expiration dates within the next 90 days are considered expiring documents."
                    }
                  >
                    <InfoCircleOutlined />
                  </Popover>
                </Space>
              }
              value={providerDocumentsStats?.expiringDocument ?? "0"}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={true}>
            <Statistic
              title="Expired count"
              value={providerDocumentsStats?.expiredDocument ?? "0"}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
      </Row>
    </Card>
  );
}
